import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Package,
  ShoppingBag,
  Users,
  LogOut,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Mail,
  Phone,
  Calendar,
  X,
  Check,
  Upload,
  BarChart3,
  FolderOpen,
  Key,
  Settings,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const additionalFileInputRef = useRef(null);
  const categoryFileInputRef = useRef(null);
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [preorders, setPreorders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Product Modal
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    image_url: "",
    images: [],
    is_featured: false,
    material: "",
    dimensions: "",
    weight: "",
  });
  
  // Category Modal
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    image: "",
  });
  
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const [uploadingCategory, setUploadingCategory] = useState(false);
  
  // Change Password Modal
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [migratingImages, setMigratingImages] = useState(false);
  const [mergingCategories, setMergingCategories] = useState(false);

  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, preordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/preorders`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setPreorders(preordersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        {
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password changed successfully!");
      setIsPasswordModalOpen(false);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleMigrateImages = async () => {
    setMigratingImages(true);
    try {
      const response = await axios.post(
        `${API}/admin/migrate-images`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `Migration complete! Updated ${response.data.products_updated} products and ${response.data.categories_updated} categories.`
      );
      // Refresh data to show updated URLs
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Migration failed");
    } finally {
      setMigratingImages(false);
    }
  };

  const handleMergeGlassOrnaments = async () => {
    setMergingCategories(true);
    try {
      const response = await axios.post(
        `${API}/admin/merge-glass-ornaments`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        `${response.data.message}. Products moved: ${response.data.products_moved}, Categories remaining: ${response.data.total_categories_remaining}`
      );
      // Refresh data to show updated categories
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Merge failed");
    } finally {
      setMergingCategories(false);
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(`${API}/upload`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });

    return `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
  };

  // ============= Product Functions =============

  const handleMainImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid image (JPEG, PNG, WebP, or GIF)");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const imageUrl = await uploadImage(file);
      setProductForm({ ...productForm, image_url: imageUrl });
      toast.success("Main image uploaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleAdditionalImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    
    for (const file of files) {
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: File too large (max 5MB)`);
        return;
      }
    }

    setUploadingAdditional(true);
    try {
      const uploadPromises = files.map(uploadImage);
      const urls = await Promise.all(uploadPromises);
      
      setProductForm({
        ...productForm,
        images: [...productForm.images, ...urls]
      });
      toast.success(`${files.length} image(s) uploaded`);
    } catch (error) {
      toast.error("Failed to upload some images");
    } finally {
      setUploadingAdditional(false);
    }
  };

  const removeAdditionalImage = (index) => {
    const newImages = productForm.images.filter((_, i) => i !== index);
    setProductForm({ ...productForm, images: newImages });
  };

  const openProductModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        description: product.description,
        price: product.price ? product.price.toString() : "",
        category: product.category,
        image_url: product.image_url,
        images: product.images || [],
        is_featured: product.is_featured,
        material: product.material || "",
        dimensions: product.dimensions || "",
        weight: product.weight || "",
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: "",
        description: "",
        price: "",
        category: "",
        image_url: "",
        images: [],
        is_featured: false,
        material: "",
        dimensions: "",
        weight: "",
      });
    }
    setIsProductModalOpen(true);
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    
    if (!productForm.name || !productForm.category) {
      toast.error("Please fill in name and category");
      return;
    }

    if (!productForm.image_url) {
      toast.error("Please add at least one main image");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...productForm,
        price: productForm.price ? parseFloat(productForm.price) : null,
        material: productForm.material || null,
        dimensions: productForm.dimensions || null,
        weight: productForm.weight || null,
        images: productForm.images || [],
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Product updated successfully");
      } else {
        await axios.post(`${API}/products`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Product created successfully");
      }

      setIsProductModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Product deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  // ============= Category Functions =============

  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid image");
      return;
    }

    setUploadingCategory(true);
    try {
      const imageUrl = await uploadImage(file);
      setCategoryForm({ ...categoryForm, image: imageUrl });
      toast.success("Category image uploaded");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingCategory(false);
    }
  };

  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        image: category.image,
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: "",
        image: "",
      });
    }
    setIsCategoryModalOpen(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    
    if (!categoryForm.name) {
      toast.error("Please enter category name");
      return;
    }

    setSaving(true);

    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/categories`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Category created");
      }

      setIsCategoryModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;

    try {
      await axios.delete(`${API}/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Category deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  // ============= Pre-Order Functions =============

  const handleUpdatePreorderStatus = async (preorderId, status) => {
    try {
      await axios.put(
        `${API}/preorders/${preorderId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDeletePreorder = async (preorderId) => {
    if (!window.confirm("Are you sure you want to delete this enquiry?")) return;

    try {
      await axios.delete(`${API}/preorders/${preorderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Enquiry deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete enquiry");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "contacted":
        return "status-contacted";
      case "completed":
        return "status-completed";
      default:
        return "status-pending";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="font-heading text-xl font-semibold text-foreground">
            Artisan Bridge <span className="text-primary">Admin</span>
          </h1>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsPasswordModalOpen(true)}
              className="text-muted-foreground hover:text-foreground"
              data-testid="change-password-btn"
            >
              <Key className="w-4 h-4 mr-2" />
              Change Password
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-foreground"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Products</p>
                <p className="text-xl font-bold text-foreground">{stats?.products_count || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Enquiries</p>
                <p className="text-xl font-bold text-foreground">{stats?.preorders_count || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Customers</p>
                <p className="text-xl font-bold text-foreground">{stats?.customers_count || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="text-xl font-bold text-foreground">{stats?.pending_preorders || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Categories</p>
                <p className="text-xl font-bold text-foreground">{categories.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="bg-muted/50 p-1 rounded-xl">
            <TabsTrigger value="products" className="rounded-lg data-[state=active]:bg-background">
              <Package className="w-4 h-4 mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger value="categories" className="rounded-lg data-[state=active]:bg-background">
              <FolderOpen className="w-4 h-4 mr-2" />
              Categories
            </TabsTrigger>
            <TabsTrigger value="preorders" className="rounded-lg data-[state=active]:bg-background">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Enquiries
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg data-[state=active]:bg-background">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-xl font-semibold text-foreground">Manage Products</h2>
              <Button
                onClick={() => openProductModal()}
                className="rounded-full bg-primary hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </div>

            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Images</TableHead>
                    <TableHead>Featured</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id} className="table-row">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                          <span className="font-medium text-foreground">{product.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">{product.category.replace(/-/g, " ")}</TableCell>
                      <TableCell className="font-medium text-primary">
                        {product.price ? `₹${product.price.toLocaleString("en-IN")}` : "-"}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {1 + (product.images?.length || 0)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {product.is_featured ? (
                          <Check className="w-5 h-5 text-secondary" />
                        ) : (
                          <X className="w-5 h-5 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => openProductModal(product)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteProduct(product.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-xl font-semibold text-foreground">Manage Categories</h2>
              <Button
                onClick={() => openCategoryModal()}
                className="rounded-full bg-primary hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <div key={category.id} className="bg-card border border-border rounded-xl overflow-hidden group">
                  <div className="aspect-video relative">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="flex gap-2">
                        <Button
                          size="icon"
                          className="w-10 h-10 rounded-full bg-background/90"
                          onClick={() => openCategoryModal(category)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          className="w-10 h-10 rounded-full bg-destructive text-white"
                          onClick={() => handleDeleteCategory(category.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground">{category.name}</h3>
                    <p className="text-xs text-muted-foreground">ID: {category.id}</p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Enquiries Tab */}
          <TabsContent value="preorders" className="space-y-4">
            <h2 className="font-heading text-xl font-semibold text-foreground">Customer Enquiries</h2>

            {preorders.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No enquiries yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {preorders.map((preorder) => (
                  <div key={preorder.id} className="bg-card border border-border rounded-xl p-6">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="font-semibold text-foreground">{preorder.customer_name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(preorder.status)}`}>
                            {preorder.status}
                          </span>
                        </div>
                        
                        <div className="space-y-2 text-sm text-muted-foreground">
                          {preorder.email && (
                            <p className="flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              <a href={`mailto:${preorder.email}`} className="hover:text-primary">
                                {preorder.email}
                              </a>
                            </p>
                          )}
                          {preorder.phone && (
                            <p className="flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              <a href={`tel:${preorder.phone}`} className="hover:text-primary">
                                {preorder.phone}
                              </a>
                            </p>
                          )}
                          <p className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {formatDate(preorder.created_at)}
                          </p>
                        </div>

                        <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                          <p className="text-sm font-medium text-foreground">Product: {preorder.product_name}</p>
                          {preorder.message && (
                            <p className="text-sm text-muted-foreground mt-1">{preorder.message}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Select
                          value={preorder.status}
                          onValueChange={(value) => handleUpdatePreorderStatus(preorder.id, value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeletePreorder(preorder.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <h2 className="font-heading text-xl font-semibold text-foreground">Settings & Utilities</h2>
            
            <div className="grid gap-6">
              {/* Image Migration Card */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  Image Migration
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Migrate product images from old URLs to cloud storage. Run this after deploying 
                  to ensure all images work correctly on production. Safe to run multiple times.
                </p>
                <Button
                  onClick={handleMigrateImages}
                  disabled={migratingImages}
                  className="bg-primary hover:bg-primary/90"
                >
                  {migratingImages ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Migrating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Run Image Migration
                    </>
                  )}
                </Button>
              </div>

              {/* Merge Glass Ornaments Card */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  Merge Glass Ornaments
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Move all Glass Ornaments products to Christmas Ornaments category and delete 
                  the Glass Ornaments category. Safe to run multiple times.
                </p>
                <Button
                  onClick={handleMergeGlassOrnaments}
                  disabled={mergingCategories}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {mergingCategories ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Merging...
                    </>
                  ) : (
                    <>
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Merge Categories
                    </>
                  )}
                </Button>
              </div>

              {/* Change Password Card */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  Change Password
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Update your admin account password.
                </p>
                <Button
                  onClick={() => setIsPasswordModalOpen(true)}
                  variant="outline"
                  className="border-border"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Change Password
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Product Modal */}
      <Dialog open={isProductModalOpen} onOpenChange={setIsProductModalOpen}>
        <DialogContent className="sm:max-w-[650px] bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingProduct ? "Edit Product" : "Add New Product"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleProductSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Product Name *</Label>
              <Input
                id="name"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                placeholder="Enter product name"
                className="h-11 rounded-lg"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select
                  value={productForm.category}
                  onValueChange={(value) => setProductForm({ ...productForm, category: value })}
                >
                  <SelectTrigger className="h-11 rounded-lg">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="price">Price (₹) - Optional</Label>
                <Input
                  id="price"
                  type="number"
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                  placeholder="Leave empty if TBD"
                  className="h-11 rounded-lg"
                />
              </div>
            </div>

            {/* Main Image Upload */}
            <div className="space-y-2">
              <Label>Main Product Image *</Label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    value={productForm.image_url}
                    onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                    placeholder="Image URL or upload file"
                    className="h-11 rounded-lg"
                  />
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleMainImageUpload}
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="h-11 px-4"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" />Upload</>}
                </Button>
              </div>
              {productForm.image_url && (
                <div className="mt-2 relative w-24 h-24 rounded-lg overflow-hidden border border-border">
                  <img src={productForm.image_url} alt="Main preview" className="w-full h-full object-cover" />
                  <span className="absolute bottom-0 left-0 right-0 bg-primary text-primary-foreground text-[10px] text-center py-0.5">Main</span>
                </div>
              )}
            </div>

            {/* Additional Images */}
            <div className="space-y-2">
              <Label>Additional Images (Gallery)</Label>
              <div className="flex gap-3">
                <input
                  type="file"
                  ref={additionalFileInputRef}
                  onChange={handleAdditionalImageUpload}
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => additionalFileInputRef.current?.click()}
                  disabled={uploadingAdditional}
                  className="w-full h-11"
                >
                  {uploadingAdditional ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add More Images
                </Button>
              </div>

              {productForm.images.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-3">
                  {productForm.images.map((img, index) => (
                    <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden border border-border group">
                      <img src={img} alt={`Additional ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeAdditionalImage(index)}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                placeholder="Product description"
                rows={3}
                className="rounded-lg resize-none"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="material">Material</Label>
                <Input
                  id="material"
                  value={productForm.material}
                  onChange={(e) => setProductForm({ ...productForm, material: e.target.value })}
                  placeholder="e.g., Brass"
                  className="h-11 rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dimensions">Dimensions</Label>
                <Input
                  id="dimensions"
                  value={productForm.dimensions}
                  onChange={(e) => setProductForm({ ...productForm, dimensions: e.target.value })}
                  placeholder="e.g., 25cm H"
                  className="h-11 rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weight">Weight</Label>
                <Input
                  id="weight"
                  value={productForm.weight}
                  onChange={(e) => setProductForm({ ...productForm, weight: e.target.value })}
                  placeholder="e.g., 850g"
                  className="h-11 rounded-lg"
                />
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <Label htmlFor="is_featured">Featured Product</Label>
              <Switch
                id="is_featured"
                checked={productForm.is_featured}
                onCheckedChange={(checked) => setProductForm({ ...productForm, is_featured: checked })}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsProductModalOpen(false)} className="flex-1 rounded-full">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 rounded-full bg-primary hover:bg-primary/90">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : editingProduct ? "Update Product" : "Add Product"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
        <DialogContent className="sm:max-w-[450px] bg-card">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingCategory ? "Edit Category" : "Add New Category"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cat-name">Category Name *</Label>
              <Input
                id="cat-name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Christmas Pillows"
                className="h-11 rounded-lg"
              />
            </div>

            <div className="space-y-2">
              <Label>Category Image</Label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    value={categoryForm.image}
                    onChange={(e) => setCategoryForm({ ...categoryForm, image: e.target.value })}
                    placeholder="Image URL or upload"
                    className="h-11 rounded-lg"
                  />
                </div>
                <input
                  type="file"
                  ref={categoryFileInputRef}
                  onChange={handleCategoryImageUpload}
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => categoryFileInputRef.current?.click()}
                  disabled={uploadingCategory}
                  className="h-11 px-4"
                >
                  {uploadingCategory ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                </Button>
              </div>
              {categoryForm.image && (
                <div className="mt-2 aspect-video max-w-[200px] rounded-lg overflow-hidden border border-border">
                  <img src={categoryForm.image} alt="Preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsCategoryModalOpen(false)} className="flex-1 rounded-full">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 rounded-full bg-primary hover:bg-primary/90">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : editingCategory ? "Update" : "Add Category"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={isPasswordModalOpen} onOpenChange={setIsPasswordModalOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                placeholder="Enter current password"
                className="h-11 rounded-lg bg-muted/50 border-transparent"
                required
                data-testid="input-current-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                placeholder="Enter new password (min 8 characters)"
                className="h-11 rounded-lg bg-muted/50 border-transparent"
                required
                data-testid="input-new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
              <Input
                id="confirmNewPassword"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
                className="h-11 rounded-lg bg-muted/50 border-transparent"
                required
                data-testid="input-confirm-new-password"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsPasswordModalOpen(false);
                  setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                }}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={changingPassword} 
                className="flex-1 rounded-full bg-primary hover:bg-primary/90"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update Password"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
